import classNames from 'classnames';
import * as React from 'react';
import { ComponentProps, ReactElement, ReactNode, isValidElement } from 'react';

import wrapChildren from '../../helpers/react/wrapChildren';
import Breakpoint from '../../types/UI/Breakpoint';
import Box from '../Box/Box';
import Column from '../Column/Column';
import Stack from '../Stack/Stack';

import useCollapseBelow from './hooks/useCollapseBelow';

import styles from './Columns.module.css';

type ColumnProps = ComponentProps<typeof Column>;

type ColumnsProps = {
    children?: ReactNode;
    align?: ComponentProps<typeof Box>['vertical'];
    gap?:
        | 'none'
        | 'xsmall'
        | 'small'
        | 'medium'
        | 'large'
        | 'xlarge'
        | 'xxlarge';
    forceCollapse?: boolean;
    collapseBelow?: Breakpoint;
    collapsedGap?: ComponentProps<typeof Stack>['space'];
    collapseReverse?: boolean;
    wrap?: boolean;
    alignCenter?: boolean;
    animatePresence?: boolean;
};

const Columns = ({
    children,
    align,
    forceCollapse = false,
    collapseBelow,
    gap = 'medium',
    collapsedGap = 'medium',
    collapseReverse = false,
    wrap = false,
    alignCenter = false,
    animatePresence = false,
}: ColumnsProps) => {
    const autoCollapsed = useCollapseBelow(collapseBelow);
    const collapsed = forceCollapse || autoCollapsed;

    const classes = classNames(styles.Root, {
        [styles.gapXsmall]: gap === 'xsmall',
        [styles.gapSmall]: gap === 'small',
        [styles.gapMedium]: gap === 'medium',
        [styles.gapLarge]: gap === 'large',
        [styles.gapXLarge]: gap === 'xlarge',
        [styles.gapXXLarge]: gap === 'xxlarge',
    });

    if (collapsed) {
        const childrenAsArray = React.Children.toArray(children);

        if (collapseReverse) {
            childrenAsArray.reverse();
        }

        return (
            <Stack
                space={collapsedGap}
                animatePresence={animatePresence}
                flex={1}
            >
                {wrapChildren(childrenAsArray, (child, key) => {
                    if (!isValidElement(child)) {
                        return <div key={key}>{child}</div>;
                    }

                    /**
                     * The `as` is an assumption, so this may cause issues
                     * down the line. At the moment, it is correct though.
                     */
                    const childAsColumn = child as ReactElement<
                        ColumnProps,
                        'Column'
                    >;

                    return React.cloneElement(childAsColumn, {
                        width: '1/1',
                    });
                })}
            </Stack>
        );
    }

    return (
        <Box
            className={classes}
            vertical={align}
            horizontal={alignCenter ? 'center' : 'left'}
            wrap={wrap}
            animatePresence={animatePresence}
        >
            {children}
        </Box>
    );
};

export default Columns;
